import React, { useEffect, useState } from 'react';
import Header from './Header'; // NOTE: Requires Header.jsx to be in the same folder
import Sidebar from './Sidebar'; // NOTE: Requires Sidebar.jsx to be in the same folder
import { FiMenu, FiX } from 'react-icons/fi';

/**
 * Main application layout for authenticated users.
 * @param {object} props
 * @param {object} props.user - The authenticated user object
 * @param {string} props.currentPage - The currently selected page key
 * @param {function} props.navigate - Function to change the current page
 * @param {function} props.handleLogout - Function to log out the user
 * @param {React.ReactNode} props.children - The content of the main page
 */
const MainLayout = ({ user, currentPage, navigate, handleLogout, children, onLeaderSelect, selectedLeader }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const handleEscToClose = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscToClose);
    return () => window.removeEventListener('keydown', handleEscToClose);
  }, [isMobileMenuOpen]);

  if (!user) return null; // Should not be rendered if not logged in

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-3 focus:py-2 focus:rounded-md focus:bg-white focus:text-indigo-700 focus:shadow"
      >
        Skip to main content
      </a>

      <Header user={user} handleLogout={handleLogout} onLeaderSelect={onLeaderSelect} />

      <button
        type="button"
        onClick={() => setIsMobileMenuOpen((open) => !open)}
        className="lg:hidden fixed top-4 left-4 z-50 inline-flex items-center justify-center w-11 h-11 rounded-lg bg-indigo-600 text-white shadow"
        aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
      >
        {isMobileMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
      </button>

      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/40"
            aria-hidden="true"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div
            className="absolute top-0 left-0 h-full w-[85%] max-w-sm px-4 pb-4 pt-16"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <Sidebar
                role={user.role === 'Main Admin' ? 'admin' : 'leader'}
                currentPage={currentPage}
                navigate={navigate}
                onNavigate={() => setIsMobileMenuOpen(false)}
                className="w-full lg:w-96"
              />
            </div>
          </div>
        </div>
      )}
      
      <div className="flex flex-col lg:flex-row max-w-[90rem] mx-auto pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8 gap-4 lg:gap-0">
        <div className="hidden lg:block">
          <Sidebar 
            role={user.role === 'Main Admin' ? 'admin' : 'leader'} 
            currentPage={currentPage} 
            navigate={navigate} 
          />
        </div>
        {/* Main Content Area */}
        <main id="main-content" className="flex-1 min-w-0 lg:ml-6 pb-12">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;